import {
  COLOR_FAMILIES,
  COLOR_STOPS,
  type ColorFamily,
  type ColorStop,
  type FoundationTokens,
  GRAY_STOPS,
  type GrayStop
} from '../../tokens/Foundation/Foundation';
import { CuratedHues, type GrayFamily } from '../CuratedHues/CuratedHues';
import { HexOklch, type OklchTriplet } from '../HexOklch/HexOklch';
import { ShadowCascadeBuilder } from '../ShadowCascadeBuilder/ShadowCascadeBuilder';
import type { ThemeDeclaration, ThemeIntensity } from '../Theme';

interface IPaletteEngine {
  derive(decl: ThemeDeclaration): { dark: FoundationTokens; light: FoundationTokens };
  deriveStops(seedHex: string, mode: 'light' | 'dark'): Record<ColorStop, string>;
}

/**
 * Anchored-mirror lightness curve. Stop 500 is identical L in both modes (the
 * anchor); stops on either side lerp linearly from the anchor to the per-mode
 * pale / deep extreme. Light mode walks 50 → pale → 950 → deep; dark mirrors.
 * The curve is the single source of truth for every colored family AND for the
 * inner gray scale (50..950).
 * `L_STOP_500_VIVID` is the opt-in anchor selected by `ThemeDeclaration.intensity`,
 * applying to the brand family only. The chroma bell peaks at stop 500, so raising
 * that stop's lightness is what makes high chroma and high lightness coincide — what
 * neon greens, yellows and cyans need and the standard anchor cannot express.
 */
const L_STOP_500_STANDARD = 0.55;
const L_STOP_500_VIVID = 0.85;
const L_PALE_LIGHT = 0.97;
const L_DEEP_LIGHT = 0.05;
const L_PALE_DARK = 0.03;
/**
 * The LIGHT gray ramp's pale half is eased, not linear. Near white the eye resolves far smaller lightness steps
 * than near black, so the linear walk that suits dark mode put light `--gray-100` at L 0.886 — two industry steps
 * too heavy, which made every light border, divider and hover built on 100–250 read as a hard grey line on white.
 * `(i / 5) ^ GRAY_LIGHT_EASE` from a paler top lands the ramp on the usual light neutral scale (100 ≈ L 0.95,
 * 200 ≈ L 0.90, 300 ≈ L 0.83). Grays only: the colored families and the whole dark ramp keep the linear curve.
 */
const L_PALE_LIGHT_GRAY = 0.985;
const GRAY_LIGHT_EASE = 1.8;
const L_DEEP_DARK = 0.95;

const L_STOP_500_BY_INTENSITY: Readonly<Record<ThemeIntensity, number>> = {
  standard: L_STOP_500_STANDARD,
  vivid: L_STOP_500_VIVID
};

/**
 * Fractional position of each stop in the anchored-mirror `0–10` curve index
 * space, shared by the colored families AND the inner gray scale (both now use
 * the same 19-stop every-50 ladder). Legacy every-100 stops keep integer
 * positions; the every-50 intermediates land on `.5` positions — guaranteeing
 * the legacy hexes don't shift now that the ladder has grown from 11 to 19.
 */
const STOP_INDEX: Readonly<Record<ColorStop, number>> = {
  '50': 0,
  '100': 1,
  '150': 1.5,
  '200': 2,
  '250': 2.5,
  '300': 3,
  '350': 3.5,
  '400': 4,
  '450': 4.5,
  '500': 5,
  '550': 5.5,
  '600': 6,
  '650': 6.5,
  '700': 7,
  '750': 7.5,
  '800': 8,
  '850': 8.5,
  '900': 9,
  '950': 10
};

const _lightnessFromPosition = (i: number, mode: 'light' | 'dark', anchor: number): number => {
  if (i === 5) return anchor;
  const pale = mode === 'light' ? L_PALE_LIGHT : L_PALE_DARK;
  const deep = mode === 'light' ? L_DEEP_LIGHT : L_DEEP_DARK;
  if (i < 5) return pale + (anchor - pale) * (i / 5);
  return anchor + (deep - anchor) * ((i - 5) / 5);
};

const _grayLightnessFromPosition = (i: number, mode: 'light' | 'dark'): number =>
  mode === 'light' && i < 5
    ? L_PALE_LIGHT_GRAY + (L_STOP_500_STANDARD - L_PALE_LIGHT_GRAY) * (i / 5) ** GRAY_LIGHT_EASE
    : _lightnessFromPosition(i, mode, L_STOP_500_STANDARD);

const _lightnessAt = (stop: ColorStop, mode: 'light' | 'dark', anchor: number): number =>
  _lightnessFromPosition(STOP_INDEX[stop], mode, anchor);

/**
 * Symmetric chroma bell centered on stop 500, shared by the colored families
 * AND the inner gray scale (both use the 19-stop every-50 ladder). The seed
 * sits at the chroma peak; both halves taper toward the extremes. Pale tints
 * (~25 % of seed chroma) read as neutral at high lightness; deep stops
 * gamut-clip to a near-neutral dark tone regardless. The every-50 intermediates
 * are the linear midpoint of their every-100 neighbours, leaving the legacy
 * values unchanged.
 */
const _CHROMA_MULT_BY_STOP: Readonly<Record<ColorStop, number>> = {
  '50': 0.25,
  '100': 0.4,
  '150': 0.5,
  '200': 0.6,
  '250': 0.7,
  '300': 0.8,
  '350': 0.875,
  '400': 0.95,
  '450': 0.975,
  '500': 1.0,
  '550': 0.975,
  '600': 0.95,
  '650': 0.875,
  '700': 0.8,
  '750': 0.7,
  '800': 0.6,
  '850': 0.5,
  '900': 0.4,
  '950': 0.25
};

/**
 * Gray's inner stops (everything except the pure-white / pure-black `0` / `1000`
 * mode anchors) are exactly the colored-family `ColorStop` set, so the gray
 * builder reuses the shared `STOP_INDEX` map directly.
 */
type GrayInnerStop = Exclude<GrayStop, '0' | '1000'>;

export const FEEDBACK_SEED_DEFAULTS: Readonly<Record<Exclude<ColorFamily, 'brand' | 'accent'>, string>> = {
  danger: '#ef4444',
  info: '#06b6d4',
  success: '#22c55e',
  warning: '#f59e0b'
};

export const PaletteEngine: IPaletteEngine = {
  /**
   * Derive light and dark foundation token blocks from a ThemeDeclaration.
   * @param decl The theme declaration.
   * @returns Foundation tokens per mode.
   */
  derive(decl: ThemeDeclaration): { dark: FoundationTokens; light: FoundationTokens } {
    const brand = _seedTriplet(decl.brand, 'brand');
    const accent = _seedTriplet(decl.accent, 'accent');
    const feedback = decl.feedback;
    const seeds: Readonly<Record<ColorFamily, OklchTriplet>> = {
      accent,
      brand,
      danger: _seedTriplet(feedback?.danger ?? FEEDBACK_SEED_DEFAULTS.danger, 'danger'),
      info: _seedTriplet(feedback?.info ?? FEEDBACK_SEED_DEFAULTS.info, 'info'),
      success: _seedTriplet(feedback?.success ?? FEEDBACK_SEED_DEFAULTS.success, 'success'),
      warning: _seedTriplet(feedback?.warning ?? FEEDBACK_SEED_DEFAULTS.warning, 'warning')
    };
    const brandAnchor = L_STOP_500_BY_INTENSITY[decl.intensity ?? 'standard'];
    return {
      dark: _buildFoundation(seeds, decl.gray, 'dark', brandAnchor),
      light: _buildFoundation(seeds, decl.gray, 'light', brandAnchor)
    };
  },
  /**
   * Derive a 19-stop scale (`50..950`) from an arbitrary hex seed using the
   * same lightness curve and chroma envelope as the named foundation families.
   * Used by the SemanticMapper to materialize stops for theme-supplied
   * diverging chart seeds (which don't get their own `--<family>-*` block in
   * `FoundationTokens`). Always uses the standard anchor — diverging seeds are
   * chart data colours, not brand identity, so a `vivid` theme must not wash them out.
   * @param seedHex Hex seed.
   * @param mode Light or dark mode.
   * @returns Per-stop hex values for the seed's ramp.
   */
  deriveStops(seedHex: string, mode: 'light' | 'dark'): Record<ColorStop, string> {
    return _buildColorScale(_seedTriplet(seedHex, 'diverging'), mode, L_STOP_500_STANDARD);
  }
};

const _seedTriplet = (hex: string, label: string): OklchTriplet => {
  const parsed = HexOklch.hexToOklch(hex);
  if (!parsed) throw new Error(`PaletteEngine: invalid ${label} seed "${hex}"`);
  return parsed;
};

const _buildColorScale = (seed: OklchTriplet, mode: 'light' | 'dark', anchor: number): Record<ColorStop, string> => {
  const result: Partial<Record<ColorStop, string>> = {};
  for (const stop of COLOR_STOPS) {
    const lightness = _lightnessAt(stop, mode, anchor);
    const chroma = seed.c * _CHROMA_MULT_BY_STOP[stop];
    result[stop] = HexOklch.oklchToHex({ c: chroma, h: seed.h, l: lightness });
  }
  return result as Record<ColorStop, string>;
};

const _buildFoundation = (
  seeds: Readonly<Record<ColorFamily, OklchTriplet>>,
  grayFamily: GrayFamily,
  mode: 'light' | 'dark',
  brandAnchor: number
): FoundationTokens => {
  const result: Partial<FoundationTokens> = {};
  const grayAnchor = CuratedHues.grayFamily(grayFamily);
  for (const stop of GRAY_STOPS) {
    /** Pure-white / pure-black mode-aware anchors. */
    if ((mode === 'light' && stop === '0') || (mode === 'dark' && stop === '1000')) {
      result[`--gray-${stop}`] = '#ffffff';
      continue;
    }
    if ((mode === 'light' && stop === '1000') || (mode === 'dark' && stop === '0')) {
      result[`--gray-${stop}`] = '#000000';
      continue;
    }
    /** Inner 19 gray stops walk the colored families' L curve (eased on the light pale half) but keep the family's chroma flat: the colored bell would strip the tint from the dark end, leaving dead-neutral panels. */
    const lightness = _grayLightnessFromPosition(STOP_INDEX[stop as GrayInnerStop], mode);
    result[`--gray-${stop}`] = HexOklch.oklchToHex({ c: grayAnchor.chroma, h: grayAnchor.hue, l: lightness });
  }
  for (const family of COLOR_FAMILIES) {
    /** Only `brand` honours the theme's intensity — accent stays rich rather than pastel, and the four feedback families stay semantically conventional. */
    const anchor = family === 'brand' ? brandAnchor : L_STOP_500_STANDARD;
    const stops = _buildColorScale(seeds[family], mode, anchor);
    for (const stop of COLOR_STOPS) {
      result[`--${family}-${stop}`] = stops[stop];
    }
  }
  const cascade = ShadowCascadeBuilder.build({ mode });
  result['--shadow-xs'] = cascade.xs;
  result['--shadow-sm'] = cascade.sm;
  result['--shadow-md'] = cascade.md;
  result['--shadow-lg'] = cascade.lg;
  result['--shadow-xl'] = cascade.xl;
  return result as FoundationTokens;
};
